<template>
  <div class="vehicles-container">
    <div class="component-header">
      <h2>Vehicle Management</h2>
      <button @click="goBack" class="back-btn">Back to Home</button>
    </div>
    
    <div class="vehicles-content">
      <!-- Left panel for vehicle list -->
      <div class="vehicles-list">
        <div class="list-header">
          <h3>Vehicles</h3>
          <button @click="showNewVehicleForm" class="add-btn">+ Add Vehicle</button>
        </div>
        
        <div v-if="isLoading" class="loading">Loading vehicles...</div>
        
        <div v-else-if="vehicles.length === 0" class="empty-state">
          No vehicles found. Add a new vehicle to get started.
        </div>
        
        <ul v-else class="vehicle-items">
          <li 
            v-for="vehicle in vehicles" 
            :key="vehicle.id" 
            :class="{ 'selected': selectedVehicle && selectedVehicle.id === vehicle.id }"
            @click="selectVehicle(vehicle)"
          >
            <div class="vehicle-item">
              <div class="vehicle-info">
                <span class="vehicle-plate">{{ vehicle.plate }}</span>
                <span class="vehicle-type">{{ vehicle.type }}</span>
                <span class="vehicle-status" :class="getStatusClass(vehicle.status)">
                  <i :class="getStatusIconClass(vehicle.status)"></i>
                  <span class="status-text">{{ getStatusText(vehicle.status) }}</span>
                </span>
              </div>
              <div class="vehicle-capacity">
                Capacity: {{ vehicle.capacity }}
              </div>
            </div>
          </li>
        </ul>
      </div>
      
      <!-- Right panel for vehicle details/form or executive dashboard -->      
      <div class="vehicle-details">
        <template v-if="isEditing || isCreating">
          <h3>{{ isCreating ? 'Add New Vehicle' : 'Edit Vehicle' }}</h3>
          
          <form @submit.prevent="saveVehicle" class="vehicle-form">
            <div class="form-group">
              <label for="plate">License Plate*</label>
              <input 
                type="text" 
                id="plate" 
                v-model="vehicleForm.plate" 
                placeholder="e.g., ABC-123" 
                required
              />
            </div>
            
            <div class="form-group">
              <label for="type">Vehicle Type*</label>
              <select id="type" v-model="vehicleForm.type" required>
                <option value="Van">Van</option>
                <option value="Truck">Truck</option>
                <option value="Car">Car</option>
                <option value="Motorcycle">Motorcycle</option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="capacity">Capacity*</label>
              <input 
                type="number" 
                id="capacity" 
                v-model.number="vehicleForm.capacity" 
                min="1" 
                step="1" 
                required
              />
            </div>
              <div class="form-row">
              <div class="form-group half">
                <label for="rent">Rental Cost (per day)</label>
                <input 
                  type="number" 
                  id="rent" 
                  v-model.number="vehicleForm.rent" 
                  min="0" 
                  step="1" 
                />
              </div>
              
              <div class="form-group half">
                <label for="maintenanceCost">Maintenance Cost</label>
                <input 
                  type="number" 
                  id="maintenanceCost" 
                  v-model.number="vehicleForm.maintenanceCost" 
                  min="0" 
                  step="0.01" 
                />
              </div>
            </div>
            <div class="form-group">
              <label for="assignedDepot">Assigned Depot</label>
              <select id="assignedDepot" v-model="vehicleForm.assignedDepot">
                <option value="">None</option>
                <option v-for="depot in depots" :key="depot.id" :value="depot.id">
                  {{ depot.name }}
                </option>
              </select>
            </div>
              <div class="form-group">
              <label for="status">Vehicle Status</label>
              <select id="status" v-model="vehicleForm.status" required>
                <option value="operational">Operational</option>
                <option value="on-duty">On Duty</option>
                <option value="maintenance">Maintenance</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            
            <div class="form-group">
              <label for="year">Year</label>
              <input
                type="number"
                id="year"
                v-model.number="vehicleForm.year"
                min="1990"
                max="2030"
                step="1"
              />
            </div>
            
            <div class="form-buttons">
              <button type="button" @click="cancelEdit" class="cancel-btn">Cancel</button>
              <button type="submit" class="save-btn">{{ isCreating ? 'Create Vehicle' : 'Update Vehicle' }}</button>
            </div>
          </form>
        </template>
        
        <template v-else-if="selectedVehicle">
          <div class="vehicle-view">
            <div class="view-header">
              <h3>{{ selectedVehicle.plate }} - {{ selectedVehicle.type }}</h3>
              <div class="action-buttons">
                <button @click="editVehicle" class="edit-btn">Edit</button>
                <button @click="confirmDelete" class="delete-btn">Delete</button>
              </div>
            </div>
              <div class="vehicle-details-content">              <div class="detail-item">
                <span class="detail-label">Status:</span>
                <span class="detail-value">
                  <i :class="getStatusIconClass(selectedVehicle.status)"></i>
                  {{ getStatusText(selectedVehicle.status) }}
                </span>
              </div>
              
              <div class="detail-item">
                <span class="detail-label">Assigned Depot:</span>
                <span class="detail-value">{{ selectedVehicle.assignedDepot || 'Not assigned' }}</span>
              </div>
              
              <div class="detail-item">
                <span class="detail-label">Capacity:</span>
                <span class="detail-value">{{ selectedVehicle.capacity }}</span>
              </div>
              
              <div class="detail-item">
                <span class="detail-label">Fuel Type:</span>
                <span class="detail-value">{{ selectedVehicle.fuelType || 'Not specified' }}</span>
              </div>
              
              <div class="detail-item">
                <span class="detail-label">Rental Cost:</span>
                <span class="detail-value">{{ selectedVehicle.rent ? `$${selectedVehicle.rent}/day` : 'Not specified' }}</span>
              </div>
              
              <div class="detail-item">
                <span class="detail-label">Maintenance Cost:</span>
                <span class="detail-value">{{ selectedVehicle.maintenanceCost ? `$${selectedVehicle.maintenanceCost}/month` : 'Not specified' }}</span>
              </div>
              
              <div class="detail-item">
                <span class="detail-label">Amortization Rate:</span>
                <span class="detail-value">{{ selectedVehicle.amortizationRate ? `${selectedVehicle.amortizationRate}% per year` : 'Not specified' }}</span>
              </div>
              
              <div class="detail-item notes" v-if="selectedVehicle.notes">
                <span class="detail-label">Notes:</span>
                <p class="detail-notes">{{ selectedVehicle.notes }}</p>
              </div>
            </div>
          </div>
        </template>
        <template v-else>          <div class="executive-dashboard">
            <h3>Depots Overview</h3>
            <div class="overview-cards">
              <DepotCard
                v-for="group in depotGroups"
                :key="group.name"
                :depotName="group.name"
                :vehicles="group.vehicles"
                :isActive="group.isActive"
                @selectVehicle="selectVehicle"
              />
            </div>
              <!-- Vehicle Status Summary -->
            <div class="status-summary">
              <h3>Vehicle Status Summary</h3>
              <div class="status-charts">
                <div class="status-chart">
                  <div class="chart-title">Vehicle Status Distribution</div>
                  <div class="status-counts">
                    <div class="status-count-item">
                      <div class="count-label operational">Active</div>
                      <div class="count-value">{{ getVehicleCountByStatus('operational') }}</div>
                    </div>
                    <div class="status-count-item">
                      <div class="count-label on-duty">On Duty</div>
                      <div class="count-value">{{ getVehicleCountByStatus('on-duty') }}</div>
                    </div>
                    <div class="status-count-item">
                      <div class="count-label maintenance">Maintenance</div>
                      <div class="count-value">{{ getVehicleCountByStatus('maintenance') }}</div>
                    </div>
                    <div class="status-count-item">
                      <div class="count-label inactive">Inactive</div>
                      <div class="count-value">{{ getVehicleCountByStatus('inactive') }}</div>
                    </div>
                  </div>
                </div>
                <div class="type-chart">
                  <div class="chart-title">Vehicle Type Distribution</div>
                  <div class="type-counts">
                    <div class="type-count-item">
                      <div class="type-label">Van</div>
                      <div class="type-value">{{ getVehicleCountByType('Van') }}</div>
                    </div>
                    <div class="type-count-item">
                      <div class="type-label">Truck</div>
                      <div class="type-value">{{ getVehicleCountByType('Truck') }}</div>
                    </div>
                    <div class="type-count-item">
                      <div class="type-label">Car</div>
                      <div class="type-value">{{ getVehicleCountByType('Car') }}</div>
                    </div>
                    <div class="type-count-item">
                      <div class="type-label">Motorcycle</div>
                      <div class="type-value">{{ getVehicleCountByType('Motorcycle') }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>        </template>
      </div>
    </div>
    
    <!-- Confirmation Dialog -->
    <div v-if="showDeleteConfirm" class="delete-confirmation-dialog">
      <div class="dialog-content">
        <h4>Confirm Deletion</h4>
        <p>Are you sure you want to delete vehicle <strong>{{ selectedVehicle.plate }}</strong>?</p>
        <p class="warning">This action cannot be undone.</p>
        <div class="dialog-buttons">
          <button @click="showDeleteConfirm = false" class="cancel-btn">Cancel</button>
          <button @click="deleteVehicle" class="confirm-delete-btn">Delete</button>
        </div>
      </div>
    </div>
    
    <div class="back-button" @click="goBack">← Back to Planning</div>
  </div>
</template>

<script>
import VehiclesService from '../services/VehiclesService';
import LocationsService from '../services/LocationsService';
import DepotCard from './DepotCard.vue';

export default {
  name: 'VehiclesComponent',
  components: { DepotCard },
  data() {
    return {
      vehicles: [],
      selectedVehicle: null,
      isLoading: true,
      isEditing: false,
      isCreating: false,
      showDeleteConfirm: false,
      depots: [],      vehicleForm: {
        plate: '',
        type: 'Van',
        rent: 0,
        maintenanceCost: 0,
        assignedDepot: '',
        year: new Date().getFullYear(),
        capacity: 1000,  // Keeping for compatibility with existing code
        status: 'operational'  // Default status
      }
    };
  },
  
  computed: {
    depotGroups() {
      // Map depot IDs to their real names
      const depotMap = {};
      this.depots.forEach(d => {
        // Add the direct id -> name mapping
        depotMap[d.id] = d.name;
        
        // Also add mapping without the "depot-" prefix if present
        if (d.id.startsWith('depot-')) {
          const numericId = d.id.replace('depot-', '');
          depotMap[numericId] = d.name;
        }
        
        // Add additional mappings that might be needed
        // Log depot info for debugging
        console.log(`Depot loaded: ID=${d.id}, Name=${d.name}`);
      });
      
      // Group vehicles by depot
      const groups = {};
      this.vehicles.forEach(vehicle => {
        // Log assignedDepot value for debugging
        if (vehicle.assignedDepot) {
          console.log(`Vehicle ${vehicle.plate} has assignedDepot=${vehicle.assignedDepot}`);
        }
        
        const depotId = vehicle.assignedDepot || '';
        const groupKey = depotId || 'unassigned';
        
        // Try to find depot name with enhanced matching
        let depotName = 'Unassigned';
        if (depotId) {
          // First try direct match
          if (depotMap[depotId]) {
            depotName = depotMap[depotId];
          } 
          // Try with adding 'depot-' prefix if it doesn't have it
          else if (!depotId.startsWith('depot-') && depotMap[`depot-${depotId}`]) {
            depotName = depotMap[`depot-${depotId}`];
          } 
          // Try without 'depot-' prefix if it has it
          else if (depotId.startsWith('depot-') && depotMap[depotId.replace('depot-', '')]) {
            depotName = depotMap[depotId.replace('depot-', '')];
          }
          // If all attempts fail, show unknown with ID for troubleshooting
          else {
            depotName = `Unknown (${depotId})`;
            console.warn(`Could not find depot name for ID: ${depotId}`);
          }
        }
        
        if (!groups[groupKey]) {
          // find the depot record to get its isActive status
          const depotObj = this.depots.find(d => d.id === depotId);
          groups[groupKey] = {
            name: depotName,
            vehicles: [],
            isActive: depotObj ? depotObj.isActive : false
          };
        }
        groups[groupKey].vehicles.push(vehicle);
      });
      
      // Convert to array and sort, putting unassigned last
      return Object.values(groups).sort((a, b) => {
        if (a.name === 'Unassigned') return 1;
        if (b.name === 'Unassigned') return -1;
        return a.name.localeCompare(b.name);
      });
    },
    
    // Add computed properties for vehicle statistics
    vehicleStats() {
      return {
        total: this.vehicles.length,
        operational: this.getVehicleCountByStatus('operational'),
        onDuty: this.getVehicleCountByStatus('on-duty'),
        maintenance: this.getVehicleCountByStatus('maintenance'),
        inactive: this.getVehicleCountByStatus('inactive')
      };
    },
    
    depotStats() {
      const depots = this.depotGroups;
      return {
        total: depots.length,
        withVehicles: depots.filter(d => d.vehicles.length > 0).length
      };
    }
  },
  
  mounted() {
    this.loadVehicles();
    this.loadDepots();
  },
  
  methods: {    loadVehicles() {
      this.isLoading = true;
        VehiclesService.getVehicles()
        .then(response => {
          // Migrate vehicles from old isActive format to new status format
          this.vehicles = response.data.map(vehicle => {
            // If vehicle has no status but has isActive, migrate it
            if (!vehicle.status && Object.prototype.hasOwnProperty.call(vehicle, 'isActive')) {
              vehicle.status = vehicle.isActive ? 'operational' : 'inactive';
            }
            // If vehicle has neither status nor isActive, default to operational
            else if (!vehicle.status) {
              vehicle.status = 'operational';
            }
            return vehicle;
          });
          this.isLoading = false;
        })
        .catch(error => {
          console.error('Error fetching vehicles:', error);
          this.isLoading = false;
        });
    },
    loadDepots() {
      LocationsService.getDepots()
        .then(response => {
          this.depots = response.data;
        })
        .catch(error => {
          console.error('Error fetching depots:', error);
        });
    },
    
    selectVehicle(vehicle) {
      if (this.isEditing || this.isCreating) {
        return; // Don't allow selection while editing or creating
      }
      
      this.selectedVehicle = vehicle;
    },
      showNewVehicleForm() {
      this.isCreating = true;
      this.isEditing = false;
      this.selectedVehicle = null;        // Reset form
      this.vehicleForm = {
        plate: '',
        type: 'Van',
        rent: 0,
        maintenanceCost: 0,
        assignedDepot: '',
        year: new Date().getFullYear(),
        capacity: 1000,  // Keeping for compatibility with existing code
        status: 'operational'  // Default status
      };
    },
    
    editVehicle() {
      if (!this.selectedVehicle) return;
      
      this.isEditing = true;
      this.isCreating = false;
      
      // Copy selected vehicle data to form
      this.vehicleForm = { ...this.selectedVehicle };
    },
    
    cancelEdit() {
      this.isEditing = false;
      this.isCreating = false;
    },
    
    saveVehicle() {
      // Generate an ID if we're creating a new vehicle
      if (this.isCreating) {
        this.vehicleForm.id = Date.now().toString();
        
        VehiclesService.createVehicle(this.vehicleForm)
          .then(response => {
            this.vehicles.push(response.data);
            this.selectedVehicle = response.data;
            this.isCreating = false;
          })
          .catch(error => {
            console.error('Error creating vehicle:', error);
            alert('Failed to create vehicle. Please try again.');
          });
      } else if (this.isEditing) {
        VehiclesService.updateVehicle(this.vehicleForm.id, this.vehicleForm)
          .then(response => {
            // Update vehicle in list
            const index = this.vehicles.findIndex(v => v.id === this.vehicleForm.id);
            if (index !== -1) {
              this.vehicles.splice(index, 1, response.data);
            }
            
            this.selectedVehicle = response.data;
            this.isEditing = false;
          })
          .catch(error => {
            console.error('Error updating vehicle:', error);
            alert('Failed to update vehicle. Please try again.');
          });
      }
    },
    
    confirmDelete() {
      this.showDeleteConfirm = true;
    },
    
    deleteVehicle() {
      if (!this.selectedVehicle) return;
      
      VehiclesService.deleteVehicle(this.selectedVehicle.id)
        .then(() => {
          // Remove vehicle from list
          this.vehicles = this.vehicles.filter(v => v.id !== this.selectedVehicle.id);
          this.selectedVehicle = null;
          this.showDeleteConfirm = false;
        })
        .catch(error => {
          console.error('Error deleting vehicle:', error);
          alert('Failed to delete vehicle. Please try again.');
        });
    },
      goBack() {
      // Emit an event to tell the parent component to switch back to the main view
      this.$emit('close-vehicles');
    },    // Status helper methods
    getStatusText(status) {
      // Handle null/undefined status by defaulting to operational
      if (!status) {
        status = 'operational';
      }
      
      const statusMap = {
        'operational': 'Operational',
        'on-duty': 'On Duty',
        'maintenance': 'Maintenance',
        'inactive': 'Inactive'
      };
      return statusMap[status] || 'Operational';
    },

    getStatusClass(status) {
      if (!status) {
        status = 'operational';
      }
      return `status-${status}`;
    },

    getStatusIconClass(status) {
      if (!status) {
        status = 'operational';
      }
      
      const iconMap = {
        'operational': 'status-icon operational',
        'on-duty': 'status-icon on-duty',
        'maintenance': 'status-icon maintenance',
        'inactive': 'status-icon inactive'
      };
      return iconMap[status] || 'status-icon operational';
    },

    // Get vehicles for specific depot
    getVehiclesByDepot(depotName) {
      if (!depotName) {
        // Handle unassigned vehicles
        return this.vehicles.filter(v => !v.assignedDepot);
      }
      return this.vehicles.filter(v => v.assignedDepot === depotName);
    },
    
    // Get vehicles by depot and status
    getVehiclesByDepotAndStatus(depotName, status) {
      return this.getVehiclesByDepot(depotName).filter(v => v.status === status);
    },
    
    // Get short status text for badges
    getStatusShortText(status) {
      if (!status) {
        status = 'operational';
      }
      
      const statusMap = {
        'operational': 'Active',
        'on-duty': 'On Duty',
        'maintenance': 'Maint.',
        'inactive': 'Inactive'
      };
      return statusMap[status] || 'Active';
    },
    
    // Count vehicles by status
    getVehicleCountByStatus(status) {
      return this.vehicles.filter(v => v.status === status).length;
    },
    
    // Count vehicles by type
    getVehicleCountByType(type) {
      return this.vehicles.filter(v => v.type === type).length;
    }
  }
};
</script>

<style scoped>
.vehicles-container {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
}

.component-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

h2 {
  margin-top: 0;
  margin-bottom: 0;
  color: #2c3e50;
  font-size: 1.8rem;
}

.back-btn {
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.back-btn:hover {
  background-color: #5a6268;
}

.vehicles-content {
  display: flex;
  flex: 1;
  gap: 20px;
  min-height: 0;
}

/* Left panel - Vehicle list */
.vehicles-list {
  flex: 0 0 300px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #eee;
}

.list-header h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #2c3e50;
}

.add-btn {
  background-color: #4ecdc4;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.add-btn:hover {
  background-color: #3dbdb5;
}

.loading, .empty-state {
  padding: 20px;
  text-align: center;
  color: #888;
}

.vehicle-items {
  list-style: none;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  flex: 1;
}

.vehicle-items li {
  padding: 12px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.vehicle-items li:hover {
  background-color: #f9f9f9;
}

.vehicle-items li.selected {
  background-color: #f0f7ff;
  border-left: 4px solid #4ecdc4;
}

.vehicle-item {
  display: flex;
  flex-direction: column;
}

.vehicle-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.vehicle-plate {
  font-weight: bold;
  color: #2c3e50;
}

.vehicle-type {
  background-color: #e9ecef;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.8rem;
  color: #6c757d;
}

.vehicle-capacity {
  font-size: 0.85rem;
  color: #6c757d;
}

/* Right panel - Vehicle details */
.vehicle-details {
  flex: 1;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  padding: 20px;
  overflow-y: auto;
}

.vehicle-details h3 {
  margin-top: 0;
  color: #2c3e50;
  font-size: 1.3rem;
  margin-bottom: 20px;
}

.empty-details {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
}

.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.edit-btn, .cancel-btn {
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
}

.delete-btn {
  background-color: #ff6b6b;
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
}

.edit-btn:hover, .cancel-btn:hover {
  background-color: #5a6268;
}

.delete-btn:hover {
  background-color: #ff5252;
}

.vehicle-details-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.detail-item {
  display: flex;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 8px;
}

.detail-label {
  flex: 0 0 180px;
  font-weight: bold;
  color: #495057;
}

.detail-notes {
  margin: 0;
  white-space: pre-line;
}

/* Form styles */
.vehicle-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 5px;
  color: #495057;
  font-weight: 500;
  font-size: 0.9rem;
}

.form-group input, 
.form-group select, 
.form-group textarea {
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 1rem;
}

.form-group input:focus, 
.form-group select:focus, 
.form-group textarea:focus {
  outline: none;
  border-color: #4ecdc4;
  box-shadow: 0 0 0 2px rgba(78, 205, 196, 0.2);
}

.form-row {
  display: flex;
  gap: 15px;
}

.form-group.half {
  flex: 1;
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 10px;
}

.save-btn {
  background-color: #4ecdc4;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.save-btn:hover {
  background-color: #3dbdb5;
}

/* Delete confirmation dialog */
.delete-confirmation-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.dialog-content {
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  width: 400px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
}

.dialog-content h4 {
  margin-top: 0;
  color: #2c3e50;
}

.warning {
  color: #ff6b6b;
  font-size: 0.9rem;
}

.dialog-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.confirm-delete-btn {
  background-color: #ff6b6b;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.confirm-delete-btn:hover {
  background-color: #ff5252;
}

/* Back button */
.back-button {
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: white;
  padding: 10px 15px;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  cursor: pointer;
  font-weight: bold;
  z-index: 1;
}

.back-button:hover {
  background: #f0f0f0;
}

/* Status Icon Styles */
.status-icon {
  font-style: normal;
  font-weight: bold;
  margin-right: 4px;
}

.status-icon.operational::before {
  content: "●";
  color: #4caf50; /* Green for operational */
}

.status-icon.on-duty::before {
  content: "●";
  color: #2196f3; /* Blue for on-duty */
}

.status-icon.maintenance::before {
  content: "●";
  color: #ff9800; /* Orange for maintenance */
}

.status-icon.inactive::before {
  content: "●";
  color: #f44336; /* Red for inactive */
}

.status-icon.small {
  font-size: 12px;
  margin-right: 2px;
}

/* Status container styles */
.vehicle-status {
  display: flex;
  align-items: center;
  gap: 4px;
}

.depot-status {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8em;
}

.status-text {
  font-size: 0.85em;
  font-weight: 500;
}

/* Status-specific text colors */
.status-operational .status-text {
  color: #4caf50;
}

.status-on-duty .status-text {
  color: #2196f3;
}

.status-maintenance .status-text {
  color: #ff9800;
}

.status-inactive .status-text {
  color: #f44336;
}

/* Additional vehicle list improvements */
.vehicle-info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.depot-vehicle-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.depot-vehicle-item:hover {
  background-color: #f5f5f5;
}

.depot-vehicle-item:last-child {
  border-bottom: none;
}

.depots-overview {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.depots-overview h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #2c3e50;
  font-size: 1.4rem;
}

.depot-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 25px;
}

.depot-card {
  flex: 1 0 30%;
  min-width: 250px;
  background-color: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: transform 0.2s, box-shadow 0.2s;
  margin-bottom: 10px;
  border: 1px solid #e0e0e0;
}

.depot-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}

.depot-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 15px;
  background-color: #f0f7ff;
  border-bottom: 1px solid #e0e0e0;
}

.depot-icon {
  font-size: 1.3rem;
}

.depot-header h4 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.depot-content {
  padding: 15px;
}

.depot-count {
  margin: 0 0 10px;
  font-size: 0.9rem;
  color: #6c757d;
}

.depot-vehicles-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
}

.depot-vehicles-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 5px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.depot-vehicles-list li:hover {
  background-color: #f0f7ff;
}

.depot-vehicles-list li:last-child {
  border-bottom: none;
}

.vehicle-plate {
  font-weight: 600;
  color: #2c3e50;
}

.depot-map {
  flex: 1;
  min-height: 300px;
  background-color: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  padding: 10px;
  border: 1px solid #e0e0e0;
}

.map-container {
  width: 100%;
  height: 300px;
  position: relative;
  background-color: #e5e3df;
  background-image: url('data:image/svg+xml;utf8,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><path d="M0 0h100v100H0z" fill="%23e5e3df"/><path d="M0 0h50v50H0zm50 50h50v50H50z" fill="%23d3d1cf"/></svg>');
  background-size: 20px 20px;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.map-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle, rgba(255,255,255,0) 30%, rgba(229,227,223,1) 100%);
}

.map-pins {
  position: relative;
  width: 100%;
  height: 100%;
}

.map-pin {
  position: absolute;
  width: 16px;
  height: 16px;
  background-color: #1e88e5;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 0 4px rgba(30, 136, 229, 0.3);
  cursor: pointer;
}

.map-pin:hover {
  background-color: #1565c0;
  box-shadow: 0 0 0 6px rgba(30, 136, 229, 0.4);
}

.map-pin::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.map-caption {
  margin: 10px 0;
  font-size: 0.9rem;
  color: #6c757d;
  text-align: center;
}

/* Executive dashboard specific styles */
.executive-dashboard {
  display: flex;
  flex-direction: column;
  gap: 25px;
  padding: 10px 0;
}

.executive-dashboard h3 {
  margin-top: 10px;
  margin-bottom: 15px;
  color: #2c3e50;
  font-size: 1.3rem;
  font-weight: 600;
}

.overview-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.overview-card {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  overflow: hidden;
  border: 1px solid #e6e6e6;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
}

.overview-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eaeaea;
}

.card-header h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
}

.card-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: rgba(78, 205, 196, 0.1);
  color: #4ecdc4;
  font-size: 1.2rem;
}

.unassigned-card .card-icon {
  background-color: rgba(255, 107, 107, 0.1);
  color: #ff6b6b;
}

.card-content {
  padding: 15px;
}

.card-count {
  margin: 0 0 12px;
  font-size: 0.95rem;
  color: #6c757d;
}

.depot-stats {
  display: flex;
  margin-bottom: 12px;
  gap: 10px;
  flex-wrap: wrap;
}

.depot-stat {
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: #f8f9fa;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 0.85rem;
}

.stat-label {
  font-weight: 500;
  color: #495057;
}

.stat-value {
  font-weight: 600;
  color: #212529;
}

.vehicle-list {
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 150px;
  overflow-y: auto;
  border-radius: 4px;
}

.vehicle-list li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 5px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  font-size: 0.9rem;
}

.vehicle-list li:hover {
  background-color: #f8f9fa;
}

.vehicle-list li:last-child {
  border-bottom: none;
}

.status-badge {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.status-operational {
  background-color: rgba(76, 175, 80, 0.15);
  color: #4caf50;
}

.status-on-duty {
  background-color: rgba(33, 150, 243, 0.15);
  color: #2196f3;
}

.status-maintenance {
  background-color: rgba(255, 152, 0, 0.15);
  color: #ff9800;
}

.status-inactive {
  background-color: rgba(244, 67, 54, 0.15);
  color: #f44336;
}

/* Removed map section CSS */

.status-summary {
  margin-top: 20px;
}

.status-charts {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.status-chart, .type-chart {
  flex: 1;
  min-width: 250px;
  padding: 15px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border: 1px solid #e6e6e6;
}

.chart-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 15px;
  text-align: center;
}

.status-counts, .type-counts {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.status-count-item, .type-count-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px;
  border-radius: 6px;
  background-color: #f8f9fa;
}

.count-label, .type-label {
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 5px;
}

.count-value, .type-value {
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
}

.count-label.operational {
  color: #4caf50;
}

.count-label.on-duty {
  color: #2196f3;
}

.count-label.maintenance {
  color: #ff9800;
}

.count-label.inactive {
  color: #f44336;
}
</style>
